import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { JobQuickActions } from "../components/JobQuickActions";
import { styleFromColor } from "../components/MonthlyTeamGrid";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { useTeamWorkdays, isOffDay, OffDayIcon } from "../hooks/useTeamWorkdays";
import {
  ArrowLeft,
  MessageSquare,
  Users,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Search,
  Menu,
  Check,
  X,
  ChevronDown,
  ListChecks,
} from "lucide-react";

const BRAND = "#249EEB";

const WEEKDAYS = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

// Paleta suave para os blocos de equipe (atribuída por ordem de equipe).
const TEAM_PALETTE = [
  { bg: "#EDE9FE", head: "#7C3AED", text: "#5B21B6", border: "#DDD6FE" }, // lavanda
  { bg: "#FDECD8", head: "#EA9A3E", text: "#B45309", border: "#FADCB4" }, // bege/laranja
  { bg: "#E1F2FD", head: BRAND, text: "#0B5C8F", border: "#BEE3F8" }, // azul claro (marca)
  { bg: "#DCFCE7", head: "#22C55E", text: "#15803D", border: "#BBF7D0" }, // verde
  { bg: "#FCE7F3", head: "#EC4899", text: "#BE185D", border: "#FBCFE8" }, // rosa
  { bg: "#FEF9C3", head: "#CA8A04", text: "#A16207", border: "#FEF08A" }, // amarelo
];
const NO_TEAM = { bg: "#F1F5F9", head: "#94A3B8", text: "#475569", border: "#E2E8F0" };

function localKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
function keyOf(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

export default function MonthlyAgenda() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const workdays = useTeamWorkdays();
  const [cursor, setCursor] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState("");
  const [teamMenuOpen, setTeamMenuOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState("");
  // Blocos por equipe começam FECHADOS; clique no cabeçalho expande.
  const [expanded, setExpanded] = useState(() => new Set());
  const [quickJob, setQuickJob] = useState(null);

  const appName = user?.company_name || user?.tenant_name || "CleanManager";

  // Grade do mês: começa no domingo da semana do dia 1 e cobre 6 semanas.
  const gridStart = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const s = new Date(first);
    s.setDate(first.getDate() - first.getDay());
    s.setHours(0, 0, 0, 0);
    return s;
  }, [cursor]);
  const gridEnd = useMemo(() => {
    const e = new Date(gridStart);
    e.setDate(gridStart.getDate() + 41);
    e.setHours(23, 59, 59, 999);
    return e;
  }, [gridStart]);

  const load = useCallback(async () => {
    const [j, c, t] = await Promise.all([
      api.get(`/jobs?from=${gridStart.toISOString()}&to=${gridEnd.toISOString()}`),
      api.get("/clients"),
      api.get("/teams"),
    ]);
    setJobs(j.data);
    setClients(c.data);
    setTeams(t.data);
  }, [gridStart, gridEnd]);
  useEffect(() => {
    load();
  }, [load]);

  const clientName = useCallback(
    (id) => clients.find((c) => c.id === id)?.name || "—",
    [clients],
  );
  const clientAddr = useCallback(
    (id) => clients.find((c) => c.id === id)?.address || "",
    [clients],
  );

  // Cor por equipe (ordem alfabética estável).
  const teamStyle = useMemo(() => {
    const sorted = [...teams].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
    const map = {};
    sorted.forEach((t, i) => {
      map[t.id] = t.color ? styleFromColor(t.color) : TEAM_PALETTE[i % TEAM_PALETTE.length];
    });
    return (id) => map[id] || NO_TEAM;
  }, [teams]);

  const teamName = useCallback(
    (id) => teams.find((t) => t.id === id)?.name || "NO TEAM",
    [teams],
  );

  // jobs -> por dia -> por equipe
  const byDay = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = {};
    for (const j of jobs) {
      if (teamFilter && j.team_id !== teamFilter) continue;
      if (q) {
        const nm = clientName(j.client_id).toLowerCase();
        const ad = clientAddr(j.client_id).toLowerCase();
        if (!nm.includes(q) && !ad.includes(q)) continue;
      }
      const day = localKey(j.scheduled_at);
      const tk = j.team_id || "__none__";
      out[day] = out[day] || {};
      out[day][tk] = out[day][tk] || [];
      out[day][tk].push(j);
    }
    for (const day of Object.keys(out)) {
      for (const tk of Object.keys(out[day])) {
        out[day][tk].sort((a, b) =>
          (a.scheduled_at || "").localeCompare(b.scheduled_at || ""),
        );
      }
    }
    return out;
  }, [jobs, teamFilter, query, clientName, clientAddr]);

  const monthLabel = cursor
    .toLocaleDateString("en-US", { month: "long", year: "numeric" })
    .toUpperCase();

  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [gridStart]);

  function shiftMonth(delta) {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  }
  function toggleExpand(k) {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  }

  async function toggleDone(e, job) {
    e.stopPropagation();
    const done = job.status === "finalizada";
    const next = done ? "agendada" : "finalizada";
    setJobs((prev) =>
      prev.map((j) => (j.id === job.id ? { ...j, status: next } : j)),
    );
    try {
      await api.patch(`/jobs/${job.id}`, { status: next });
    } catch (err) {
      setJobs((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, status: job.status } : j)),
      );
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao atualizar");
    }
  }

  const monthIdx = cursor.getMonth();

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* HEADER */}
      <header
        className="h-14 flex items-center px-3 text-white"
        style={{ background: BRAND }}
        data-testid="monthly-header"
      >
        <div className="flex items-center gap-1 w-32">
          <button
            onClick={() => navigate("/schedule")}
            data-testid="monthly-back"
            className="p-2 rounded-lg hover:bg-white/15 transition-colors"
            title="Voltar"
          >
            <ArrowLeft size={20} />
          </button>
          <Link
            to="/notifications"
            className="p-2 rounded-lg hover:bg-white/15 transition-colors"
            title="Mensagens"
          >
            <MessageSquare size={20} />
          </Link>
        </div>
        <div className="flex-1 text-center font-bold tracking-wide truncate">
          {appName}
        </div>
        <div className="flex items-center gap-1 w-32 justify-end">
          <Link
            to="/teams"
            className="p-2 rounded-lg hover:bg-white/15 transition-colors"
            title="Equipes"
          >
            <Users size={20} />
          </Link>
          <Link
            to="/schedule"
            className="p-2 rounded-lg hover:bg-white/15 transition-colors"
            title="Agenda"
          >
            <CalendarIcon size={20} />
          </Link>
        </div>
      </header>

      {/* BARRA DE CONTROLES */}
      <div className="bg-white border-b border-slate-200 px-3 py-2 flex items-center gap-2 flex-wrap sticky top-0 z-20">
        <div className="relative">
          <button
            onClick={() => setTeamMenuOpen((o) => !o)}
            data-testid="monthly-all-teams"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-white text-xs font-bold uppercase tracking-wide hover:bg-slate-900 transition-colors"
          >
            <Users size={15} />
            {teamFilter ? teamName(teamFilter) : "All Teams"}
            <ChevronDown size={14} />
          </button>
          {teamMenuOpen && (
            <div className="absolute left-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-slate-200 z-30 py-1 max-h-72 overflow-y-auto">
              <button
                onClick={() => {
                  setTeamFilter("");
                  setTeamMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
              >
                {!teamFilter && <Check size={14} className="text-blue-600" />}
                <span className={!teamFilter ? "font-semibold" : ""}>Todas as equipes</span>
              </button>
              {[...teams]
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((t) => (
                  <button
                    key={t.id}
                    onClick={() => {
                      setTeamFilter(t.id);
                      setTeamMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                  >
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ background: teamStyle(t.id).head }}
                    />
                    <span className={teamFilter === t.id ? "font-semibold" : ""}>
                      {t.name}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>

        <Link
          to="/schedule"
          data-testid="monthly-schedule-btn"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-white text-xs font-bold uppercase tracking-wide hover:bg-slate-900 transition-colors"
        >
          <ListChecks size={15} />
          Schedule
        </Link>

        {/* Navegação de mês */}
        <div className="flex items-center gap-1 mx-auto bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => shiftMonth(-1)}
            data-testid="monthly-prev"
            className="p-1.5 rounded-md hover:bg-white transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <div
            className="px-4 text-sm font-bold text-slate-700 min-w-[140px] text-center"
            data-testid="monthly-label"
          >
            {monthLabel}
          </div>
          <button
            onClick={() => shiftMonth(1)}
            data-testid="monthly-next"
            className="p-1.5 rounded-md hover:bg-white transition-colors"
          >
            <ChevronRight size={18} />
          </button>
          <button
            onClick={() => setCursor(new Date())}
            data-testid="monthly-today"
            className="ml-1 px-2.5 py-1 rounded-md text-xs font-semibold text-slate-700 hover:bg-white transition-colors"
          >
            Hoje
          </button>
        </div>

        <div className="flex items-center gap-1">
          {showSearch && (
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar cliente…"
              data-testid="monthly-search-input"
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 w-44"
            />
          )}
          <button
            onClick={() => setShowSearch((s) => !s)}
            data-testid="monthly-search-toggle"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
          >
            <Search size={18} />
          </button>
          <Link
            to="/hoje"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            title="Menu"
          >
            <Menu size={18} />
          </Link>
        </div>
      </div>

      {/* CABEÇALHO + GRADE — rolagem horizontal no celular */}
      <div className="lg:hidden px-3 py-1 text-[11px] font-medium text-slate-500 bg-slate-50 border-b border-slate-200 text-center">
        ← deslize para o lado para ver todos os dias →
      </div>
      <div className="flex-1 overflow-auto">
        <div className="min-w-[900px] lg:min-w-0">
          <div className="grid grid-cols-7 bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wider sticky top-0 z-10">
            {WEEKDAYS.map((w) => (
              <div key={w} className="px-2 py-2 text-center border-r border-blue-500 last:border-0">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 auto-rows-fr">
          {cells.map((d) => {
            const dayKey = keyOf(d);
            const inMonth = d.getMonth() === monthIdx;
            const dayGroups = byDay[dayKey] || {};
            const jobCount = Object.values(dayGroups).reduce(
              (s, arr) => s + arr.length,
              0,
            );
            const teamKeys = Object.keys(dayGroups).sort((a, b) => {
              if (a === "__none__") return 1;
              if (b === "__none__") return -1;
              return teamName(a).localeCompare(teamName(b));
            });
            return (
              <div
                key={dayKey}
                data-testid={`monthly-day-${dayKey}`}
                className={`min-h-[130px] border-b border-r border-slate-200 p-1.5 ${
                  inMonth ? "bg-white" : "bg-slate-50/70"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-slate-500">
                    {jobCount > 0 ? `${jobCount} ${jobCount === 1 ? "Job" : "Jobs"}` : ""}
                  </span>
                  <span
                    className={`text-sm font-bold ${
                      inMonth ? "text-slate-800" : "text-slate-400"
                    }`}
                  >
                    {d.getDate()}
                  </span>
                </div>

                <div className="space-y-1.5">
                  {teamKeys.map((tk) => {
                    const arr = dayGroups[tk];
                    const style = tk === "__none__" ? NO_TEAM : teamStyle(tk);
                    const name = tk === "__none__" ? "NO TEAM" : teamName(tk);
                    const ckey = `${dayKey}:${tk}`;
                    const isExpanded = expanded.has(ckey);
                    const offDay = isOffDay(workdays, tk, d) && arr.some((j) => j.status !== "cancelada");
                    return (
                      <div
                        key={ckey}
                        className="rounded-md overflow-hidden border"
                        style={{ borderColor: style.border, background: style.bg }}
                        data-testid={`monthly-teamblock-${dayKey}-${tk}`}
                      >
                        <button
                          onClick={() => toggleExpand(ckey)}
                          className="w-full flex items-center justify-between px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white"
                          style={{ background: style.head }}
                        >
                          <span className="truncate flex items-center gap-1">
                            {offDay && (
                              <span className="bg-white rounded-full p-0.5" title="Dia em que a equipe normalmente não trabalha">
                                <OffDayIcon size={11} testId={`monthly-offday-${dayKey}-${tk}`} />
                              </span>
                            )}
                            {name}
                          </span>
                          <span className="flex items-center gap-1 shrink-0">
                            <span className="bg-white/25 rounded-full px-1.5">
                              {arr.length}
                            </span>
                            <ChevronDown
                              size={12}
                              className={`transition-transform ${
                                isExpanded ? "" : "-rotate-90"
                              }`}
                            />
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="p-1 space-y-0.5">
                            {arr.map((job) => {
                              const done = job.status === "finalizada";
                              return (
                                <JobItem
                                  key={job.id}
                                  job={job}
                                  done={done}
                                  offDay={offDay && job.status !== "cancelada"}
                                  label={clientName(job.client_id)}
                                  textColor={style.text}
                                  onToggle={(e) => toggleDone(e, job)}
                                  onOpen={(anchorRef) =>
                                    setQuickJob({ job, anchorRef })
                                  }
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {quickJob && (
        <JobQuickActions
          job={quickJob.job}
          anchorRef={quickJob.anchorRef}
          teams={teams}
          onClose={() => setQuickJob(null)}
          onMutated={() => {
            setQuickJob(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function JobItem({ job, done, offDay, label, textColor, onToggle, onOpen }) {
  const ref = useRef(null);
  const cancelled = job.status === "cancelada";
  const time = job.scheduled_at
    ? new Date(job.scheduled_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  return (
    <div
      ref={ref}
      onClick={() => onOpen(ref)}
      data-testid={`monthly-job-${job.id}`}
      className="flex items-center gap-1.5 px-1 py-0.5 rounded cursor-pointer hover:bg-white/60 transition-colors"
    >
      <button
        onClick={onToggle}
        data-testid={`monthly-check-${job.id}`}
        className={`w-4 h-4 rounded flex items-center justify-center border shrink-0 transition-colors ${
          cancelled
            ? "bg-white border-red-500 text-red-600"
            : done
              ? "bg-emerald-500 border-emerald-500 text-white"
              : "bg-white border-slate-300 hover:border-emerald-400"
        }`}
        title={cancelled ? "Cancelada" : done ? "Concluído" : "Pendente"}
      >
        {cancelled ? <X size={12} strokeWidth={3.5} /> : done ? <Check size={11} strokeWidth={3} /> : null}
      </button>
      <span
        className={`text-[11px] flex items-center gap-1 min-w-0 ${
          cancelled ? "opacity-90" : done ? "line-through opacity-60" : ""
        }`}
        style={{
          color: cancelled ? "#dc2626" : textColor,
          ...(cancelled
            ? { textDecorationLine: "line-through", textDecorationStyle: "double", textDecorationColor: "#dc2626" }
            : {}),
        }}
      >
        {time && (
          <span className="font-bold tabular-nums shrink-0">{time}</span>
        )}
        <span className="truncate">{label}</span>
      </span>
      {offDay && <OffDayIcon size={12} testId={`monthly-offday-job-${job.id}`} />}
    </div>
  );
}
